import { strEnum } from "../../types";
import { IFlogoContributionDisplay } from "./flogo-connection";
// import {IFlogoContributionDisplay} from "./connector";
// tslint:disable:max-line-length
/**
 * FieldType string enumeration
 * @type {{[K in string | string | string | string]:K}}
 */
export const FlogoFieldType = strEnum(["string", "number", "boolean", "complex_object", "array", "object", "integer"]);
export type FlogoFieldType = keyof typeof FlogoFieldType;

/**
 *
 */
export const FlogoDisplayFieldType = strEnum(["connection", "dropdown", "table", "texteditor", "fileselector", "connectiondata", "button", "params", "password"]);
export type FlogoDisplayFieldType = keyof typeof FlogoDisplayFieldType;


/**
 * Button Type string enumeration
 * @type {{[K in string|string|string]:K}}
 */
export const FlogoButtonType = strEnum(["submit", "reset", "button"]);
export type FlogoButtonType = keyof typeof FlogoButtonType;
/**
 *  Button Style string enumeration
 * @type {{[K in string|string|string|string|string|string]:K}}
 */
export const FlogoButtonStyle = strEnum(["default", "primary", "success", "info", "warning", "danger"]);
export type FlogoButtonStyle = keyof typeof FlogoButtonStyle;

export enum FLOGO_CONTRIB_MODE {
    WIZARD,
    PALETTE
}

/**
 * @desc This interface defines the contract between the value provider of a field
 * whose display field type is {{DisplayFieldType.connection}}.
 */
export interface IFlogoConnectionAllowedValue {
    /**
     * @desc This is the unique id assigned to a new connection during creation
     * @example uuid
     */
    unique_id: string;
    /**
     * This is a name which is either calculated or provided via field value
     * in the connection JSON settings. In absence of name unique_id will be
     * used for display as a drop down choice.
     */
    name: string;
}

/**
 * The UI form sends the IContributionContext
 */
export interface IFlogoContributionContext {
    changelist?: string[];
    appId?: string;
    currentFlowName?: string;

    hashTags?: string[];

    /**
     * Returns the mode whether it is from Wizard or palette.
     */
    getMode(): FLOGO_CONTRIB_MODE;

    /**
     * Returns the flowname for the given context
     */
    getCurrentFlowName(): string;
}

export interface IFlogoActionDefinition {
    /**
     * Unique name to identify the button. If display name is not provided name will be used.
     */
    name: string;
    /**
     * Button display settings
     */
    display?: IFlogoFieldDisplay;
}

/**
 * Field Definition
 */
export interface IFlogoFieldDefinition {
    /**
     * name of the field, not same as display name
     */
    name: string;
    /**
     * data type of the field in string e.g. string,number
     */
    type: FlogoFieldType;
    /**
     * value of the field
     */
    value?: any;
    /**
     * specifies if the form field is required
     */
    required?: boolean;
    /**
     * specified allowed value for display field type "dropdown", keeping it same as flogo
     * For field display type == {{DisplayFieldType.connections}} then allowed property should
     * return array of {{IConnectionAllowedValue}}
     */
    allowed?: string[] | IFlogoConnectionAllowedValue[];
    /**
     * Used to place the field in a customized form tab section
     */
    section?: string;
    /**
     * Description
     */
    description: string;
    /**
     * Field display settings
     */
    display?: IFlogoFieldDisplay;
    /**
     * Field display settings for wizard
     */
    wizard?: IFlogoFieldWizardDisplay;
}


/**
 * Flogo Connector Contribution
 */
export interface IFlogoConnectorContribution extends IFlogoContributionContext {
    /**
     * Connector name. Must be unique, no spaces
     */
    name: string;
    /**
     *  Title to display on the connection tile
     */
    title: string;
    /**
     * Connector version. Must follow semver versioning
     */
    version: string;
    /**
     * Type of contribution flogo:activity or flogo:trigger or flogo:connector
     */
    type: string;
    /**
     * URI reference for connector
     */
    ref: string;
    /**
     * Provider the field name in settings section which will hold identifiable
     * name for the connection. In the absence of this keyfield the unique
     * connection record identifier e.g. uuid will be used for display.
     */
    keyfield: string;
    /**
     * Display Settings
     */
    display?: IFlogoContributionDisplay;
    /**
     * Static connector configurations e.g. connection name, connection description, connection configuration etc.
     *

     * FOR {{AUTHENTICATION_TYPE}} IS {{AUTHENTICATION_TYPE.OAUTH1 }} | {{AUTHENTICATION_TYPE.OAUTH2 }} | {{AUTHENTICATION_TYPE.OAUTH2_TWOLEG }}
     * FIXED ReEntry Field Name **WI_STUDIO_OAUTH_CONNECTOR_INFO** is mandatory.
     *
     * This field is not required for other Authentication purposes where it does not
     * involve a round trip to the third party auth system with a callback
     * The user can save all the required authentication tokens in this field which is
     * then used by the activity contribution developer to use this information.
     *
     * {
     *   "name" : "WI_STUDIO_OAUTH_CONNECTOR_INFO",
     *   "type" : "string",
     *   "display": {
     *      "visible": false
     *   }
     *  }
     */
    settings: IFlogoFieldDefinition[];
    /**
     * Form Action configuration
     */
    actions: IFlogoActionDefinition[];

    /**
     * Returns a field from inputs,outputs. Field Names must be unique within the activity
     * @param name
     */
    getField(name: string): IFlogoFieldDefinition;

}




/**
 * Field display settings
 */
export interface IFlogoFieldDisplay {
    /**
     * Named used in display
     */
    name?: string;
    /**
     * Display field type
     */
    type?: FlogoDisplayFieldType;
    /**
     * Used with display field type dropdown
     */
    selection?: string;
    /**
     * Descrption for the field
     */
    description?: string;
    /**
     * Used with display field type fileselector
     */
    fileExtensions?: string[];
    /**
     * true when field is also shown in mapper, else false
     */
    mappable?: boolean;
    /**
     *  Used with display field type texteditor
     */
    syntax?: string;
    /**
     * Used with display field type table
     */
    schema?: string;
    /**
     * specifies if the field is visible , the field is always enabled. The default is true.
     */
    visible?: boolean;
    /**
     * Specifies the button type. Default type is "button"
     */
    buttonType?: FlogoButtonType;
    /**
     * Button Styles when display type is button. Default style is "btn-default"
     */
    buttonStyle?: FlogoButtonStyle;
    /**
     * Sets the widget in read-only mode
     */
    readonly?: boolean;
    /**
     * Indicates whether the field is valid or not
     */
    valid?: boolean;
    /**
     * specifies if the form field is exportable
     */
    exportable?: boolean;
    /**
     * specifies if the form field data is encryptable
     */
    encryptable?: boolean;

    // TODO: Connections are using this, need to revisit
    uri?: string;

    /**
     * Display in only mapper but not on input settings/ output settings even though there is display section
     */
    settings?: boolean;

    /**
     * Display in only Input Settings but not in mapper
     */
    inputSettingsOnly?: boolean;

    /**
     * Display in only Output Settings but not in output tree
     */
    outputSettingsOnly?: boolean;

    /**
     * Application Property Support , this is to be used with
     * fields showing primary data types i.e. number, boolean, string, date , time
     * and not for complex type
     */
    appPropertySupport?: boolean;
    /**
     * Determines if the user has choosed to substitute and app property for the field value.
     */
    appPropertyEnabled?: boolean;
}


/**
 * Field display settings for wizard
 */
export interface IFlogoFieldWizardDisplay {
    /**
     * Wizard Step Label
     */
    step: string;
    /**
     * Field Display Name to be shown on wizard page
     */
    name?: string;
    /**
     * Field Widget type
     */
    type?: string;
    /**
     * inline CSS for the field item
     */
    css?: any;
}
